import { Anchor, Card, Stack, Badge, Tabs, Button, Group, Drawer, Title, Text, Divider, NumberInput, Textarea, TextInput, Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconRefresh, IconAlertTriangle, IconSearch, IconCalendar } from "@tabler/icons-react";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useAction, useInfiniteResource } from "../../../shared/api";
import { PageHeader, MoneyDisplay, EmptyState, formatDateTime, InfiniteTable } from "../../../shared/ui";
import { OrderDetailDrawer } from "./Orders";

/** Date → "YYYY-MM-DD" (lokal alanlardan; toISOString UTC'ye kaydırıp yanlış
 *  güne düşürmesin). Backend bu günü Europe/Istanbul gün sınırı olarak yorumlar. */
function toLocalDateStr(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  PENDING: "yellow",
  SUCCEEDED: "green",
  COMPLETED: "green",
  FAILED: "red",
  REFUNDED: "violet",
  PARTIALLY_REFUNDED: "orange",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PENDING: "Beklemede",
  SUCCEEDED: "Başarılı",
  COMPLETED: "Başarılı",
  FAILED: "Başarısız",
  REFUNDED: "İade Edildi",
  PARTIALLY_REFUNDED: "Kısmi İade",
};

const TENDER_TYPE_LABEL: Record<string, string> = {
  CASH: "Nakit",
  CARD: "Kart",
  QR: "QR",
  VOUCHER: "Yemek Çeki",
  ONLINE_RESERVATION: "Online Rezervasyon",
  STORE_CREDIT: "Sadakat Bakiyesi",
};

// İade tipi seçenekleri — backend payment.models.RefundType ile birebir.
const REFUND_TYPE_OPTIONS = [
  { value: "CASH_REFUND", label: "Nakit İade" },
  { value: "CARD_REFUND", label: "Karta Geri Yatırma" },
  { value: "STORE_CREDIT", label: "Mağaza Kredisi" },
  { value: "PRODUCT_EXCHANGE", label: "Ürün Değişimi" },
  { value: "COMPLIMENTARY", label: "İkram (Comp)" },
  { value: "ADJUSTMENT", label: "Düzeltme" },
];

const DISPUTE_COLOR: Record<string, string> = {
  OPEN: "red", UNDER_REVIEW: "orange", WON: "green", LOST: "red", ACCEPTED: "gray",
};

const DISPUTE_STATUS_LABEL: Record<string, string> = {
  OPEN: "Açık",
  UNDER_REVIEW: "İncelemede",
  WON: "Kazanıldı",
  LOST: "Kaybedildi",
  ACCEPTED: "Kabul Edildi",
};

export function PaymentsPage() {
  const [refundPayment, setRefundPayment] = useState<any | null>(null);
  // Sipariş detayını bu sayfada YAN PANEL olarak aç — başka ekrana redirect YOK.
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  return (
    <Stack gap="md">
      <PageHeader
        title="Ödemeler & İadeler"
        subtitle="Müşteri tahsilatları (nakit/kart/QR) ve iadeler. Gider için → Gelir-Gider, nakit kasa sayımı için → Kasa Hareketleri."
      />
      <Tabs defaultValue="payments">
        <Tabs.List>
          <Tabs.Tab value="payments">Ödemeler</Tabs.Tab>
          <Tabs.Tab value="refunds">İadeler</Tabs.Tab>
          <Tabs.Tab value="disputes">Disputes</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="payments" pt="md">
          <PaymentsList onRefund={setRefundPayment} onOpenOrder={setOpenOrderId} />
        </Tabs.Panel>
        <Tabs.Panel value="refunds" pt="md">
          <RefundsList />
        </Tabs.Panel>
        <Tabs.Panel value="disputes" pt="md">
          <DisputesList />
        </Tabs.Panel>
      </Tabs>

      <RefundDrawer payment={refundPayment} onClose={() => setRefundPayment(null)} />
      {/* Sipariş detayı — Siparişler ekranındaki aynı drawer, bu sayfada açılır. */}
      <OrderDetailDrawer
        orderId={openOrderId}
        onClose={() => setOpenOrderId(null)}
        onOpenOrder={setOpenOrderId}
      />
    </Stack>
  );
}

function PaymentsList({ onRefund, onOpenOrder }: { onRefund: (p: any) => void; onOpenOrder: (id: string) => void }) {
  const [sessionNo, setSessionNo] = useState("");
  const [debouncedSessionNo] = useDebouncedValue(sessionNo.trim(), 300);
  // Tarih aralığı OPSİYONEL: boşken davranış değişmez (tüm geçmiş, en yeni 20 +
  // aşağı kaydırdıkça yükleme). Aralık seçilince queryset daralır ama infinite
  // scroll aynen çalışır (date_from/date_to filtreye eklenir, sayfalama korunur).
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const filters: Record<string, any> = {};
  if (debouncedSessionNo) filters.session_no = debouncedSessionNo;
  if (dateRange[0]) filters.date_from = toLocalDateStr(dateRange[0]);
  if (dateRange[1]) filters.date_to = toLocalDateStr(dateRange[1]);
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("payments", { filters });

  return (
    <Stack gap="sm">
      <Group>
        <TextInput
          placeholder="Adisyon No ara"
          leftSection={<IconSearch size={16} />}
          value={sessionNo}
          onChange={(e) => setSessionNo(e.currentTarget.value)}
          w={220}
        />
        <DatePickerInput
          type="range"
          placeholder="Tarih aralığı (tümü)"
          leftSection={<IconCalendar size={16} />}
          value={dateRange}
          onChange={(v) => setDateRange(v as [Date | null, Date | null])}
          clearable
          w={260}
        />
      </Group>
      <Card padding="sm" radius="md" withBorder>
      <InfiniteTable
        rows={list}
        rowKey={(p) => p.id}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        emptyContent={<EmptyState title="Ödeme yok" />}
        columns={[
          { header: "Tarih", render: (p) => formatDateTime(p.created_at) },
          { header: "Tip", render: (p) => <Badge variant="light">{TENDER_TYPE_LABEL[p.tender_type] || p.tender_type}</Badge> },
          {
            header: "Sipariş",
            render: (p) =>
              p.order ? (
                <Anchor component="button" type="button" onClick={() => onOpenOrder(p.order)}>
                  {p.order_label || "Sipariş"}
                </Anchor>
              ) : (
                <Text span c="dimmed" size="xs">{p.order_label || "-"}</Text>
              ),
          },
          {
            // Adisyon (TableSession) — aynı adisyonu gören N ödeme satırı
            // "tek müşterinin sipariş turlarının ödemeleri". Split ödemede
            // de aynı Session altında N farklı Payment görünür.
            header: "Adisyon",
            render: (p) =>
              p.table_session_id ? (
                <code style={{ fontSize: 11 }}>
                  #{String(p.table_session_id).slice(0, 8)}
                </code>
              ) : (
                <Text span c="dimmed" size="xs">—</Text>
              ),
          },
          { header: "Tutar", align: "right", render: (p) => <Text fw={600} span><MoneyDisplay minor={p.amount_minor} /></Text> },
          {
            header: "Durum",
            render: (p) => (
              <Badge color={PAYMENT_STATUS_COLOR[p.status] || "gray"}>
                {PAYMENT_STATUS_LABEL[p.status] || p.status}
              </Badge>
            ),
          },
          {
            header: "",
            render: (p) =>
              p.status === "SUCCEEDED" || p.status === "COMPLETED" ? (
                <Button size="xs" variant="light" color="violet" leftSection={<IconRefresh size={12} />} onClick={() => onRefund(p)}>
                  İade
                </Button>
              ) : null,
          },
        ]}
      />
      </Card>
    </Stack>
  );
}

function RefundsList() {
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("refunds");

  return (
    <Card padding="sm" radius="md" withBorder>
      <InfiniteTable
        rows={list}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        emptyContent={<EmptyState title="İade yok" />}
        columns={[
          { header: "Tarih", render: (r) => formatDateTime(r.created_at) },
          { header: "Ödeme", render: (r) => <code style={{ fontSize: 11 }}>{r.payment?.toString().slice(0, 8)}</code> },
          {
            header: "İade Tutarı",
            align: "right",
            render: (r) => <Text fw={600} span c="violet"><MoneyDisplay minor={r.amount_minor} /></Text>,
          },
          { header: "Sebep", render: (r) => r.reason || "-" },
        ]}
      />
    </Card>
  );
}

function DisputesList() {
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("disputes");

  return (
    <Card padding="sm" radius="md" withBorder>
      <InfiniteTable
        rows={list}
        rowKey={(d) => d.id}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        emptyContent={<EmptyState title="Dispute yok" icon="✓" />}
        columns={[
          { header: "Tarih", render: (d) => formatDateTime(d.created_at) },
          { header: "Ödeme", render: (d) => <code style={{ fontSize: 11 }}>{d.payment?.toString().slice(0, 8)}</code> },
          { header: "Sebep", render: (d) => <Badge variant="light">{d.reason}</Badge> },
          {
            header: "Durum",
            render: (d) => (
              <Badge color={DISPUTE_COLOR[d.status] || "gray"}>
                {DISPUTE_STATUS_LABEL[d.status] || d.status}
              </Badge>
            ),
          },
          {
            header: "Yanıt Süresi",
            render: (d) => {
              const dueSoon = d.response_due_at && new Date(d.response_due_at).getTime() - Date.now() < 86400000 * 3;
              return (
                <Text span {...(dueSoon ? { c: "red" } : {})}>
                  {d.response_due_at ? formatDateTime(d.response_due_at) : "-"}
                  {dueSoon && <IconAlertTriangle size={14} style={{ marginLeft: 4, verticalAlign: "middle" }} />}
                </Text>
              );
            },
          },
          {
            header: "Tutar",
            align: "right",
            render: (d) => <Text fw={600} span><MoneyDisplay minor={d.disputed_amount_minor || 0} /></Text>,
          },
        ]}
      />
    </Card>
  );
}

function RefundDrawer({ payment, onClose }: { payment: any | null; onClose: () => void }) {
  const refund = useAction("payments", "refund");
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState("");
  const [refundType, setRefundType] = useState<string | null>("ADJUSTMENT");
  const [opened, { open, close }] = useDisclosure(false);

  // Open when payment is provided
  if (payment && !opened) {
    setAmount(payment.net_amount_minor || payment.amount_minor);
    open();
  }

  async function handleSubmit() {
    if (!payment) return;
    try {
      await refund.mutateAsync({ id: payment.id, amount_minor: amount, reason, refund_type: refundType });
      notifications.show({ message: "İade başarılı", color: "green" });
      close();
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Drawer opened={opened && !!payment} onClose={() => { close(); onClose(); }} position="right" title="İade İşlemi">
      {payment && (
        <Stack gap="md">
          <Card padding="md" radius="md" withBorder>
            <Title order={5}>Orijinal Ödeme</Title>
            <Divider my="sm" />
            <Group justify="space-between"><Text c="dimmed">Tip:</Text><Badge>{TENDER_TYPE_LABEL[payment.tender_type] || payment.tender_type}</Badge></Group>
            <Group justify="space-between"><Text c="dimmed">Tutar:</Text><MoneyDisplay minor={payment.amount_minor} fw={700} /></Group>
            <Group justify="space-between"><Text c="dimmed">Net:</Text><MoneyDisplay minor={payment.net_amount_minor || payment.amount_minor} /></Group>
          </Card>

          <NumberInput
            label="İade Tutarı"
            value={amount / 100}
            onChange={(v) => setAmount(Math.round((Number(v) || 0) * 100))}
            min={0.01}
            max={payment.amount_minor / 100}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator="."
            decimalSeparator=","
            suffix=" ₺"
            size="lg"
          />
          <Select
            label="İade Tipi"
            data={REFUND_TYPE_OPTIONS}
            value={refundType}
            onChange={setRefundType}
            allowDeselect={false}
          />
          <Textarea label="Sebep" value={reason} onChange={(e) => setReason(e.currentTarget.value)} minRows={3} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => { close(); onClose(); }}>İptal</Button>
            <Button color="violet" onClick={handleSubmit} loading={refund.isPending}>İade Et</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}
